import { useEffect, useState } from 'react';
import { collection, query, where, getDocs, Timestamp, deleteDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { toast } from 'sonner';

// Utility function to convert Western numerals to Arabic-Indic numerals
const toArabicNumerals = (str: string) => {
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return str.replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
};
import { Loader2, CalendarDays, Edit, X, MoreHorizontal, Pause, Play, Trash2, RefreshCw } from 'lucide-react';
import { AvailabilityScheduler } from './AvailabilityScheduler';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface Availability {
  id: string;
  startDate: Timestamp;
  endDate: Timestamp;
  selectedDays: { [key: number]: string[] };
  status: 'pending' | 'approved' | 'rejected';
  isHeld?: boolean;
}

export function CoachAvailabilityList() {
  const [allAvailabilities, setAllAvailabilities] = useState<Availability[]>([]);
  const [loading, setLoading] = useState(true);
  const [showScheduler, setShowScheduler] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState<Availability | null>(null);
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const { t, dir, language } = useLanguage();
  const dateLocale = language === 'ar' ? ar : enUS;

  useEffect(() => {
    fetchAvailabilities();
  }, [currentUser]);

  const fetchAvailabilities = async () => {
    if (!currentUser?.uid) return;

    try {
      const availabilitiesRef = collection(db, 'availabilities');
      const q = query(
        availabilitiesRef,
        where('coachId', '==', currentUser.uid)
      );
      
      const querySnapshot = await getDocs(q);
      
      const availabilityData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Availability[];

      setAllAvailabilities(availabilityData);
    } catch (error) {
      console.error('Error fetching availabilities:', error);
      toast.error(t('dashboard.coach.availability.errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const renderSelectedDays = (selectedDays: { [key: number]: string[] } | undefined) => {
    if (!selectedDays) return null;
    
    const weekDays = [
      t('dashboard.coach.availability.days.sunday'),
      t('dashboard.coach.availability.days.monday'),
      t('dashboard.coach.availability.days.tuesday'),
      t('dashboard.coach.availability.days.wednesday'),
      t('dashboard.coach.availability.days.thursday'),
      t('dashboard.coach.availability.days.friday'),
      t('dashboard.coach.availability.days.saturday')
    ];
    
    return Object.entries(selectedDays).map(([day, sessions]) => {
      if (!sessions || !Array.isArray(sessions)) return null;
      return (
        <Badge key={day} variant="secondary" className={`${dir === 'rtl' ? 'ml-1' : 'mr-1'} mb-1`}>
          {weekDays[Number(day)]}: {language === 'ar' 
            ? toArabicNumerals(sessions.map(s => t(`dashboard.coach.availability.sessions.${s}`)).join(', '))
            : sessions.map(s => t(`dashboard.coach.availability.sessions.${s}`)).join(', ')
          }
        </Badge>
      );
    });
  };

  const getAvailabilityStatus = (availability: Availability) => {
    const now = new Date();
    const endDate = availability.endDate.toDate();
    
    if (availability.status === 'pending') {
      return 'pending';
    } else if (availability.status === 'approved' && endDate > now) {
      return 'active';
    } else {
      return 'expired';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="secondary" className="bg-orange-100 text-orange-800">
            {t('dashboard.coach.availability.status.pending')}
          </Badge>
        );
        case 'active':
          return (
            <Badge variant="secondary" className={`${dir === 'rtl' ? 'ml-1' : 'mr-1'} mb-1`}>
              {t('dashboard.coach.availability.status.active')}
            </Badge>
          );
      case 'expired':
        return (
          <Badge variant="secondary" className="bg-gray-100 text-gray-600">
            {t('dashboard.coach.availability.status.expired')}
          </Badge>
        );
      default:
        return null;
    }
  };

  const handleWithdraw = async (availabilityId: string) => {
    try {
      setWithdrawing(availabilityId);
      await deleteDoc(doc(db, 'availabilities', availabilityId));
      toast.success(t('dashboard.coach.availability.withdraw.success'));
      fetchAvailabilities(); // Refresh the list
    } catch (error) {
      console.error('Error withdrawing availability:', error);
      toast.error(t('dashboard.coach.availability.withdraw.error'));
    } finally {
      setWithdrawing(null);
    }
  };

  const handleDelete = async (availabilityId: string) => {
    try {
      setWithdrawing(availabilityId);
      await deleteDoc(doc(db, 'availabilities', availabilityId));
      toast.success(t('dashboard.coach.availability.delete.success'));
      fetchAvailabilities(); // Refresh the list
    } catch (error) {
      console.error('Error deleting availability:', error);
      toast.error(t('dashboard.coach.availability.delete.error'));
    } finally {
      setWithdrawing(null);
    }
  };

  const handleHoldToggle = async (availabilityId: string, currentHoldStatus: boolean) => {
    try {
      setWithdrawing(availabilityId);
      // In a real implementation, you would update the document with the hold status
      // For now, we'll just show a success message
      const action = currentHoldStatus ? 'unhold' : 'hold';
      toast.success(t(`dashboard.coach.availability.${action}.success`));
      fetchAvailabilities(); // Refresh the list
    } catch (error) {
      console.error('Error toggling hold status:', error);
      toast.error(t('dashboard.coach.availability.hold.error'));
    } finally {
      setWithdrawing(null);
    }
  };

  const handleRenew = async (availabilityId: string) => {
    try {
      setWithdrawing(availabilityId);
      // In a real implementation, you would renew the expired availability
      // For now, we'll just show a success message
      toast.success(t('dashboard.coach.availability.renew.success'));
      fetchAvailabilities(); // Refresh the list
    } catch (error) {
      console.error('Error renewing availability:', error);
      toast.error(t('dashboard.coach.availability.renew.error'));
    } finally {
      setWithdrawing(null);
    }
  };

  const handleEdit = (availability: Availability) => {
    setEditingAvailability(availability);
    setShowScheduler(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="p-3 md:p-4 lg:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <div className="space-y-0.5 md:space-y-1">
            <CardTitle className="flex items-center gap-1.5 md:gap-2 text-base md:text-lg">
              <CalendarDays className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
              {t('dashboard.coach.availability.title')}
            </CardTitle>
            <p className="text-xs md:text-sm text-muted-foreground">
              {t('dashboard.coach.availability.description')}
            </p>
          </div>
          <Button 
            onClick={() => {
              setEditingAvailability(null);
              setShowScheduler(true);
            }}
            className="w-full sm:w-auto text-xs md:text-sm h-8 md:h-9"
            size="sm"
          >
            <CalendarDays className={`h-3.5 w-3.5 md:h-4 md:w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
            <span className="hidden sm:inline">{t('dashboard.coach.availability.actions.submitNew')}</span>
            <span className="sm:hidden">Submit</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-3 md:p-4 lg:p-6 pt-0">

      {showScheduler ? (
          <div className="border rounded-lg p-3 md:p-4">
            <h4 className="font-semibold mb-3 md:mb-4 text-sm md:text-base">
              {editingAvailability 
                ? t('dashboard.coach.availability.actions.edit')
                : t('dashboard.coach.availability.scheduler.title')
              }
            </h4>
            <AvailabilityScheduler 
              onSubmitSuccess={() => {
                setShowScheduler(false);
                setEditingAvailability(null);
                fetchAvailabilities();
              }}
              onCancel={() => {
                setShowScheduler(false);
                setEditingAvailability(null);
              }}
              editingAvailability={editingAvailability}
            />
          </div>
        ) : allAvailabilities.length === 0 ? (
          <div className="text-center py-6 md:py-8">
            <div className="flex flex-col items-center justify-center text-center space-y-3 md:space-y-4">
              <CalendarDays className="h-10 w-10 md:h-12 md:w-12 text-muted-foreground" />
              <div className="space-y-1 md:space-y-2">
                <h3 className="font-semibold tracking-tight text-sm md:text-base">{t('dashboard.coach.availability.noSchedules.title')}</h3>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {t('dashboard.coach.availability.noSchedules.description')}
                </p>
              </div>
              <Button 
                onClick={() => {
                  setEditingAvailability(null);
                  setShowScheduler(true);
                }}
                className="text-xs md:text-sm h-8 md:h-9"
                size="sm"
              >
                {t('dashboard.coach.availability.actions.submitFirst')}
              </Button>
            </div>
          </div>
      ) : (
        <div className="grid gap-3 md:gap-4">
          {allAvailabilities.map((availability) => {
            const status = getAvailabilityStatus(availability);
            return (
            <Card key={availability.id}>
              <CardContent className="p-3 md:p-4 lg:pt-6">
                <div className="flex flex-col gap-2 md:gap-3">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-2 md:gap-4">
                      {/* Content - Left Side */}
                      <div className="flex-1 min-w-0 space-y-1.5 md:space-y-2">
                        <p className="text-xs md:text-sm font-medium">
                          {language === 'ar' 
                            ? toArabicNumerals(`${format(availability.startDate.toDate(), 'PPP', { locale: dateLocale })} - ${format(availability.endDate.toDate(), 'PPP', { locale: dateLocale })}`)
                            : `${format(availability.startDate.toDate(), 'PPP', { locale: dateLocale })} - ${format(availability.endDate.toDate(), 'PPP', { locale: dateLocale })}`
                          }
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {renderSelectedDays(availability.selectedDays)}
                        </div>
                      </div>
                      
                      {/* Status Badge & Actions - Right Side */}
                      <div className="flex items-center gap-1.5 md:gap-2 sm:flex-shrink-0">
                        {/* Status Badge */}
                        <div className="flex-shrink-0">
                          {getStatusBadge(status)}
                        </div>
                        
                        {/* Action Buttons - Removed separate edit button for active status */}
                        
                        {/* Dropdown Menu */}
                        {(status === 'pending' || status === 'active' || status === 'expired') && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={withdrawing === availability.id}
                                className="h-7 w-7 md:h-8 md:w-8 p-0 flex-shrink-0"
                              >
                                {withdrawing === availability.id ? (
                                  <Loader2 className="h-3.5 w-3.5 md:h-4 md:w-4 animate-spin" />
                                ) : (
                                  <MoreHorizontal className="h-3.5 w-3.5 md:h-4 md:w-4" />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {/* Pending: Only Withdraw */}
                              {status === 'pending' && (
                                <DropdownMenuItem
                                  onClick={() => handleWithdraw(availability.id)}
                                  className="text-orange-600"
                                >
                                  <X className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                                  {t('dashboard.coach.availability.withdraw.button')}
                                </DropdownMenuItem>
                              )}
                              
                              {/* Active: Edit, Hold/Unhold and Delete */}
                              {status === 'active' && (
                                <>
                                  <DropdownMenuItem
                                    onClick={() => handleEdit(availability)}
                                  >
                                    <Edit className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                                    {t('dashboard.coach.availability.actions.edit')}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleHoldToggle(availability.id, availability.isHeld || false)}
                                  >
                                    {availability.isHeld ? (
                                      <>
                                        <Play className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                                        {t('dashboard.coach.availability.unhold.button')}
                                      </>
                                    ) : (
                                      <>
                                        <Pause className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                                        {t('dashboard.coach.availability.hold.button')}
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleDelete(availability.id)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                                    {t('dashboard.coach.availability.delete.button')}
                                  </DropdownMenuItem>
                                </>
                              )}
                              
                              {/* Expired: Renew and Delete */}
                              {status === 'expired' && (
                                <>
                                  <DropdownMenuItem
                                    onClick={() => handleRenew(availability.id)}
                                    className="text-green-600"
                                  >
                                    <RefreshCw className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                                    {t('dashboard.coach.availability.renew.button')}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleDelete(availability.id)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                                    {t('dashboard.coach.availability.delete.button')}
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
        )}
      </CardContent>
    </Card>
  );
}